import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  Divider,
  Button
} from '@mui/material';
import { 
  Search as SearchIcon,
  BugReport as TicketIcon,
  Business as AccountIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import JiraIcon from '../icons/JiraIcon';

import LandingPageSearch from './LandingPageSearch';
import styles from './jira.module.css';

const JiraSearchLanding = () => {
  const navigate = useNavigate();
  const [recentSearches, setRecentSearches] = React.useState([]);

  useEffect(() => {
    document.title = 'Jira Search - GSD Support Admin';
    // Load recent searches from localStorage
    const savedSearches = localStorage.getItem('jiraRecentSearches');
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
    return () => {
      document.title = 'GSD Support Admin';
    };
  }, []);

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const searchTypes = [
    {
      icon: <TicketIcon sx={{ fontSize: 40, color: '#28BCBB' }} />,
      title: 'Ticket ID Search',
      description: 'Search for specific Jira tickets using their ticket ID',
      example: 'Supports: MWTR-767018, PREMIUM-122, KB-130',
      color: '#28BCBB'
    },
        {
      icon: <SearchIcon sx={{ fontSize: 40, color: '#28BCBB' }} />,
      title: 'Salesforce ID Search',
      description: 'Search tickets using Salesforce opportunity IDs',
      example: 'Examples: 0015f00001G7EscAAF, 0015f00001G6qAzAAJ',
      color: '#28BCBB'
    }
    ,
    {
      icon: <AccountIcon sx={{ fontSize: 40, color: '#28BCBB' }} />,
      title: 'Account/Opportunity ID Search', 
      description: 'Find all tickets associated with accounts or opportunities',
      example: 'Examples: AccId: 2251037, OppId: 3379333',
      color: '#28BCBB'
    },

  ];

  return (
    <div 
      className={styles.Jira} 
      style={{ 
        fontFamily: 'Inter, Roboto, Arial, sans-serif', 
        background: '#f6f8fa',
        minHeight: '100vh', 
        padding: '32px 16px' 
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box mb={4}>
          <Button
            onClick={() => navigate('/')}
            variant="contained"
            size="small"
            sx={{
              minWidth: '40px',
              marginBottom: '16px',
              padding: '6px',
              backgroundColor: '#1d9f9f',
              color: 'white',
              boxShadow: '0 2px 6px rgba(29, 159, 159, 0.25)',
              '&:hover': {
                backgroundColor: '#188182',
                boxShadow: '0 3px 8px rgba(29, 159, 159, 0.3)'
              },
              '&:active': {
                backgroundColor: '#167172',
                boxShadow: '0 1px 4px rgba(29, 159, 159, 0.2)'
              }
            }}
          >
            <HomeIcon fontSize="small" />
          </Button>
          
          <Paper 
            elevation={3}
            component="main"
            role="main"
            aria-label="Jira Search Interface"
            sx={{
              background: '#fff',
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
              border: '2px solid #28bcbb'
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <JiraIcon sx={{ fontSize: 48, mr: 2 }} />
              <Typography 
                variant="h3" 
                fontWeight={700}
                sx={{
                  color: '#1a1a1a'
                }}
              >
                Jira Search
              </Typography>
            </Box>
            
              <Typography 
                variant="h6" 
                color="text.secondary" 
                mb={4}
                sx={{ 
                  maxWidth: '600px', 
                  mx: 'auto',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  lineHeight: 1.5,
                  letterSpacing: '0.00938em'
                }}
              >
                Search for tickets by ID, Salesforce ID or Account/Opportunity ID. 
                Input type is automatically detected.
              </Typography>       
            <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
              <LandingPageSearch />
              <Box mt={2} minHeight="40px">
                <div id="search-status" role="status" aria-live="polite">
                  
                </div>
              </Box>
            </Box>

          </Paper>
        </Box>

        {/* Search Type Information Cards */}
        <Box mb={4}>
          <Typography 
            variant="h4" 
            fontWeight={600} 
            textAlign="center" 
            mb={4}
            sx={{ color: '#1a1a1a' }}
          >
            Search Options
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {searchTypes.map((type, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  elevation={3}
                  role="article"
                  tabIndex={0}
                  onKeyPress={(e) => handleKeyPress(e, () => {})}
                  aria-label={`${type.title} search information`}
                  sx={{
                    height: '100%',
                    background: '#fff',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    border: '2px solid #28bcbb',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(40, 188, 187, 0.15)',
                      borderColor: '#1e9b9b'
                    },
                    '&:focus': {
                      outline: '3px solid #28bcbb',
                      outlineOffset: '2px'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Box mb={2}>
                      {type.icon}
                    </Box>
                    
                    <Typography variant="h6" fontWeight={600} mb={2}>
                      {type.title}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      {type.description}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontStyle: 'italic',
                        color: type.color,
                        fontWeight: 500
                      }}
                    >
                      {type.example}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Recent Searches Section */}
        {recentSearches.length > 0 && (
          <Box mb={4}>
            <Paper 
              elevation={3}
              component="section"
              role="complementary"
              aria-label="Recent Searches"
              sx={{
                background: '#fff',
                borderRadius: 3,
                p: 3,
                border: '2px solid #28bcbb'
              }}
            >
              <Typography variant="h6" fontWeight={600} mb={2}>
                Recent Searches
              </Typography>
              <Grid container spacing={1}>
                {recentSearches.map((search, index) => (
                  <Grid item key={index}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/jira/search/${search}`)}
                      sx={{
                        borderColor: '#28bcbb',
                        color: '#28bcbb',
                        '&:hover': {
                          borderColor: '#1e9b9b',
                          backgroundColor: 'rgba(40, 188, 187, 0.04)'
                        }
                      }}
                    >
                      {search}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Quick Tips Section */}
        <Paper 
          elevation={3}
          component="section"
          role="complementary"
          aria-label="Quick Tips"
          sx={{
            background: '#fff',
            borderRadius: 3,
            p: 3,
            border: '2px solid #28bcbb'
          }}
        >
          <Typography 
            variant="h6" 
            fontWeight={600} 
            mb={2}
            sx={{ color: '#1a1a1a' }}
          >
            💡 Quick Tips
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Access recent searches from the dropdown menu
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Press Enter or use dropdown options to search
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Results include ticket details and related information
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </div>
  );
};

export default JiraSearchLanding;